import React from 'react';
import { CheckCircle2, Building2, TrendingUp, Users, Shield, Coins } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardHeader, CardTitle, CardDescription } from '../components/ui/card';

const UeberUns = () => {
  const schwerpunkte = [
    'Gestaltung und Optimierung von Holding- und Beteiligungsstrukturen',
    'Strukturierung von Immobilienvermögen und Projektgesellschaften',
    'Steuerliche und rechtliche Begleitung von Private Investments',
    'Beratung im Bereich Krypto-Assets und digitale Vermögensstrukturen',
    'Entwicklung von Nachfolge- und Vermögenssicherungskonzepten',
  ];

  const mandanten = [
    'Unternehmer mit wachsendem/strukturiertem Vermögen',
    'Gesellschafter von Kapital- und Holdinggesellschaften',
    'Immobilieninvestoren mit komplexen Portfolios',
    'Investoren im Bereich Krypto und digitale Assets',
    'Vermögende Privatpersonen mit Family-Office-Strukturen',
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-brand-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-brand-900 mb-6 leading-tight">
            Strategische Vermögensarchitektur
          </h1>
          <p className="text-xl text-brand-700 leading-relaxed">
            Wir verbinden juristische Präzision, steuerliche Expertise und unternehmerisches Denken 
            zu ganzheitlichen Vermögensstrukturen.
          </p>
        </div>
      </section>

      {/* Die Kanzlei Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-900 mb-8 text-center">
            Die Kanzlei
          </h2>
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-brand-700 leading-relaxed mb-6">
              <strong className="text-brand-900">DR GRASSL & Coll</strong> steht für die Verbindung aus juristischer Präzision, 
              steuerlicher Expertise und unternehmerischem Denken. Wir verstehen Vermögen nicht nur rechtlich, 
              sondern strategisch.
            </p>
            <p className="text-lg text-brand-700 leading-relaxed mb-6">
              Wir arbeiten dort, wo Strukturierungsbedarf besteht – nicht im Massengeschäft. 
              Typischerweise begleiten wir Strukturen <strong className="text-brand-900">ab 5 Mio. € bis in den dreistelligen Millionenbereich</strong>.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
              <div className="text-center p-6 bg-brand-50 rounded-xl">
                <div className="text-3xl font-bold text-brand-900 mb-2">5 Mio. €</div>
                <div className="text-brand-700 text-sm">Mindestvolumen</div>
              </div>
              <div className="text-center p-6 bg-brand-50 rounded-xl">
                <div className="text-3xl font-bold text-brand-900 mb-2">100+ Mio. €</div>
                <div className="text-brand-700 text-sm">Expertise bis</div>
              </div>
              <div className="text-center p-6 bg-brand-50 rounded-xl">
                <div className="text-3xl font-bold text-brand-900 mb-2">40+</div>
                <div className="text-brand-700 text-sm">Jahre Erfahrung</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Leistungsfelder Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-brand-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-900 mb-12 text-center">
            Unsere Schwerpunkte
          </h2>
          <div className="space-y-4">
            {schwerpunkte.map((punkt, index) => (
              <div 
                key={index}
                className="flex items-start space-x-4 p-6 bg-white rounded-xl border-2 border-brand-200 hover:border-brand-400 transition-all duration-300 hover:shadow-md"
              >
                <CheckCircle2 className="w-6 h-6 text-brand-700 flex-shrink-0 mt-0.5" />
                <p className="text-lg text-brand-800">{punkt}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mandantenprofil Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-900 mb-6 text-center">
            Unsere Mandanten
          </h2>
          <p className="text-xl text-brand-700 text-center mb-12 max-w-3xl mx-auto">
            Wer profitiert von unserer Expertise?
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mandanten.map((mandant, index) => (
              <Card key={index} className="border-2 border-brand-200 hover:border-brand-400 transition-all duration-300 hover:shadow-lg">
                <CardHeader className="p-6">
                  <div className="mb-4">
                    {index === 0 && <Building2 className="w-10 h-10 text-brand-700" />}
                    {index === 1 && <TrendingUp className="w-10 h-10 text-brand-700" />}
                    {index === 2 && <Building2 className="w-10 h-10 text-brand-700" />}
                    {index === 3 && <Coins className="w-10 h-10 text-brand-700" />}
                    {index === 4 && <Shield className="w-10 h-10 text-brand-700" />}
                  </div>
                  <CardDescription className="text-base text-brand-800 leading-relaxed">
                    {mandant}
                  </CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
          <div className="mt-12 p-6 bg-brand-50 rounded-xl text-center">
            <p className="text-lg text-brand-800">
              <strong className="text-brand-900">Fokus:</strong> Nachhaltige und rechtssichere Lösungen sowie Diskretion.
            </p>
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-brand-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-900 mb-12 text-center">
            Praxisbeispiele
          </h2>
          
          {/* Case Study 1 - Krypto */}
          <div className="mb-12 bg-white rounded-2xl shadow-lg overflow-hidden border-2 border-brand-200">
            <div className="bg-brand-900 text-white p-6">
              <h3 className="text-2xl font-bold mb-2">Case Study: Krypto-Vermögen</h3>
              <p className="text-brand-100">Steuerliche Strukturierung digitaler Assets</p>
            </div>
            <div className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h4 className="text-lg font-bold text-brand-900 mb-3 flex items-center">
                    <span className="w-2 h-2 bg-red-500 rounded-full mr-3"></span>
                    Ausgangslage
                  </h4>
                  <p className="text-brand-700 mb-4">
                    Investor mit <strong>1,2 Mio. € Gewinnen</strong> aus Krypto-Investments ohne klare 
                    steuerliche Einordnung und Dokumentation.
                  </p>
                  <h4 className="text-lg font-bold text-brand-900 mb-3 flex items-center mt-6">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mr-3"></span>
                    Probleme
                  </h4>
                  <ul className="space-y-2 text-brand-700">
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Steuerliche Unsicherheit</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Fehlende Transaktionshistorie</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Risiken bei Auszahlung</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-brand-900 mb-3 flex items-center">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>
                    Lösung
                  </h4>
                  <ul className="space-y-2 text-brand-700 mb-6">
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Aufarbeitung Transaktionshistorie</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Steuerliche Einordnung</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Implementierung Dokumentationssystem</span>
                    </li>
                  </ul>
                  <h4 className="text-lg font-bold text-brand-900 mb-3 flex items-center mt-6">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                    Ergebnis
                  </h4>
                  <div className="p-4 bg-green-50 rounded-lg border-l-4 border-green-500">
                    <p className="text-brand-800 font-medium">
                      Steuerliche Transparenz, Risikoreduzierung und Planungssicherheit für künftige Liquidität.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Case Study 2 - Family Office */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden border-2 border-brand-200">
            <div className="bg-brand-900 text-white p-6">
              <h3 className="text-2xl font-bold mb-2">Case Study: Family Office</h3>
              <p className="text-brand-100">Strukturierung komplexer Vermögensarchitekturen</p>
            </div>
            <div className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h4 className="text-lg font-bold text-brand-900 mb-3 flex items-center">
                    <span className="w-2 h-2 bg-red-500 rounded-full mr-3"></span>
                    Ausgangslage
                  </h4>
                  <p className="text-brand-700 mb-4">
                    Unternehmer mit Beteiligungen, Immobilien und liquiden Mitteln 
                    <strong> (&gt;25 Mio. €)</strong> ohne übergeordnete Struktur.
                  </p>
                  <h4 className="text-lg font-bold text-brand-900 mb-3 flex items-center mt-6">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mr-3"></span>
                    Probleme
                  </h4>
                  <ul className="space-y-2 text-brand-700">
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Fragmentierung der Vermögenswerte</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Steuerliche Ineffizienz</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Keine Nachfolgeplanung</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Fehlende Gesamtarchitektur</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-brand-900 mb-3 flex items-center">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>
                    Lösung
                  </h4>
                  <ul className="space-y-2 text-brand-700 mb-6">
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Konzeption Holdingstruktur</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Bündelung der Vermögenswerte</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Entwicklung Nachfolge- und Sicherungsstrategie</span>
                    </li>
                  </ul>
                  <h4 className="text-lg font-bold text-brand-900 mb-3 flex items-center mt-6">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                    Ergebnis
                  </h4>
                  <div className="p-4 bg-green-50 rounded-lg border-l-4 border-green-500">
                    <p className="text-brand-800 font-medium">
                      Steuerlich optimierte Gesamtstruktur, verbesserte Steuerungsfähigkeit und 
                      solide Grundlage für den Generationenübergang.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Case Study 3 - Immobilienportfolio */}
          <div className="mt-12 bg-white rounded-2xl shadow-lg overflow-hidden border-2 border-brand-200">
            <div className="bg-brand-900 text-white p-6">
              <h3 className="text-2xl font-bold mb-2">Case Study: Immobilienportfolio</h3>
              <p className="text-brand-100">Strukturierung eines Immobilienportfolios</p>
            </div>
            <div className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h4 className="text-lg font-bold text-brand-900 mb-3 flex items-center">
                    <span className="w-2 h-2 bg-red-500 rounded-full mr-3"></span>
                    Ausgangssituation
                  </h4>
                  <p className="text-brand-700 mb-4">
                    Ein Unternehmer hielt mehrere Immobilien im Privatvermögen 
                    <strong> (Gesamtvolumen ca. 8 Mio. €)</strong>. Weitere Zukäufe waren geplant, 
                    eine strukturierte Gesamtstrategie fehlte.
                  </p>
                  <h4 className="text-lg font-bold text-brand-900 mb-3 flex items-center mt-6">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mr-3"></span>
                    Problemstellung
                  </h4>
                  <ul className="space-y-2 text-brand-700">
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Hohe laufende Steuerbelastung</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Keine klare Trennung einzelner Objekte</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Risiko bei Verkauf und Reinvestition</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Fehlende Nachfolgestruktur</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-brand-900 mb-3 flex items-center">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>
                    Unsere Lösung
                  </h4>
                  <ul className="space-y-2 text-brand-700 mb-6">
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Aufbau einer Holdingstruktur mit Objektgesellschaften</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Steueroptimierte Einbringung bestehender Immobilien</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Strukturierung zukünftiger Ankäufe über Gesellschaften</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Vorbereitung flexibler Exit-Optionen</span>
                    </li>
                  </ul>
                  <h4 className="text-lg font-bold text-brand-900 mb-3 flex items-center mt-6">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                    Ergebnis
                  </h4>
                  <div className="p-4 bg-green-50 rounded-lg border-l-4 border-green-500">
                    <p className="text-brand-800 font-medium">
                      Deutlich verbesserte steuerliche Struktur, klare Trennung und Skalierbarkeit 
                      des Portfolios, optimierte Reinvestitionsmöglichkeiten und rechtssichere 
                      Grundlage für weiteres Wachstum.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default UeberUns;
